import { CAMPAIGNS_QUERY } from "@lib/graphql/queries";
import type { Campaign } from "@lib/types";
import { STATUS_LABELS } from "@lib/types";
import { Building2, Database, Target, Users } from "lucide-react";
import { Link } from "react-router";
import { useQuery } from "urql";

const SECTION_TILES = [
  {
    to: "/campaigns",
    label: "Campaigns",
    description: "Active searches and pipelines",
    icon: Target,
  },
  {
    to: "/clients",
    label: "Clients",
    description: "Accounts and engagements",
    icon: Building2,
  },
  {
    to: "/candidates",
    label: "Candidates",
    description: "Everyone in your universe",
    icon: Users,
  },
  {
    to: "/talent-pools",
    label: "Talent Pools",
    description: "Curated lists by sector or skill",
    icon: Database,
  },
];

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    on_hold: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    completed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] || colors.draft}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function Dashboard() {
  // Overview panel: one large page (field max_page_size 250), not paginated.
  const [{ data, fetching }] = useQuery({ query: CAMPAIGNS_QUERY, variables: { first: 250 } });
  const campaigns: Campaign[] = (data?.campaigns?.edges ?? []).map(
    (edge: { node: Campaign }) => edge.node,
  );
  const activeCampaigns = campaigns.filter((c) => c.status === "active");

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-text-primary">Dashboard</h1>
      <p className="mt-1 text-sm text-text-muted">Your home base across every section</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {SECTION_TILES.map(({ to, label, description, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="group flex flex-col gap-3 rounded-lg bg-card p-5 shadow-sm ring-1 ring-ring transition-colors hover:bg-card-hover"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-light text-amber-800 dark:text-amber-400">
              <Icon size={18} />
            </div>
            <div>
              <p className="text-sm font-semibold text-text-primary group-hover:text-accent">
                {label}
              </p>
              <p className="mt-0.5 text-xs text-text-muted">{description}</p>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border-t-2 border-accent bg-card p-5 shadow-sm ring-1 ring-ring">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Active Campaigns
          </p>
          <p className="mt-1 text-3xl font-bold text-text-primary">{activeCampaigns.length}</p>
        </div>
        <div className="rounded-lg bg-card p-5 shadow-sm ring-1 ring-ring">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Total Campaigns
          </p>
          <p className="mt-1 text-3xl font-bold text-text-primary">{campaigns.length}</p>
        </div>
        <div className="rounded-lg bg-card p-5 shadow-sm ring-1 ring-ring">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Total Candidates
          </p>
          <p className="mt-1 text-3xl font-bold text-text-primary">
            {campaigns.reduce((sum, c) => sum + c.candidateCount, 0)}
          </p>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">Recent Campaigns</h2>
          <Link to="/campaigns" className="text-sm font-medium text-accent hover:text-accent-hover">
            View all
          </Link>
        </div>

        {fetching ? (
          <p className="mt-4 text-sm text-text-muted">Loading...</p>
        ) : campaigns.length === 0 ? (
          <div className="mt-4 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
            <p className="text-sm text-text-muted">No campaigns yet</p>
            <Link
              to="/campaigns"
              className="mt-2 inline-block text-sm font-medium text-accent hover:text-accent-hover"
            >
              Create your first campaign
            </Link>
          </div>
        ) : (
          <div className="mt-4 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-card-alt">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Stage
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Candidates
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {campaigns.slice(0, 10).map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-card-hover">
                    <td className="px-4 py-3">
                      <Link
                        to={`/campaigns/${campaign.id}`}
                        className="font-medium text-accent hover:text-accent-hover"
                      >
                        {campaign.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {campaign.client.name}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={campaign.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {campaign.currentStage}/9
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {campaign.candidateCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
